import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import FeaturedProducts from "../components/FeaturedProducts";
import { seoDefaults } from "../utils/seoDefaults";
import { motion, AnimatePresence } from "framer-motion";
import levelup from "../assests/levelup.webp"
import buildyour from "../assests/buildyour.jpg"
import gamerparadise from "../assests/gamers-paradise-is-amazing.jpg"
import SEO from "../api/seo";


const slides = [
  {
    title1: "GAMING",
    title2: "PARADISE",
    desc: (
      <>
        Your one-stop destination for{" "}
        <span className="text-cyan-300">consoles</span>,{" "}
        <span className="text-purple-400">game CDs</span>, and premium{" "}
        <span className="text-cyan-300">accessories</span>.
      </>
    ),
    img: gamerparadise,
    gradient: "from-cyan-500 to-purple-600",
  },
  {
    title1: "LEVEL UP",
    title2: "YOUR EXPERIENCE",
    desc: (
      <>
        Discover next-gen gaming with{" "}
        <span className="text-purple-400">4K graphics</span> and{" "}
        <span className="text-cyan-300">immersive sound</span>.
      </>
    ),
    img: levelup,
    gradient: "from-purple-500 to-pink-500",
  },
  {
    title1: "BUILD YOUR",
    title2: "ULTIMATE SETUP",
    desc: (
      <>
        Choose from top-tier{" "}
        <span className="text-cyan-300">gaming gear</span> and{" "}
        <span className="text-purple-400">RGB accessories</span>.
      </>
    ),
    img: buildyour,
    gradient: "from-blue-500 to-cyan-500",
  },
];

const Homepage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
 const [index, setIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const slide = slides[index];

  return (
    <>
    <SEO {...seoDefaults.home} />

    <div className="min-h-screen bg-gray-950 text-white">
      {/* Hero Section */}
<section
  id="home"
  className="relative overflow-hidden pt-32 pb-20 px-6 bg-gradient-to-b from-[#0a0a0f] to-[#101018]"
>
  {/* glowing animated bg */}
  <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-purple-900/20 rounded-full filter blur-3xl animate-pulse"></div>
  <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-cyan-900/15 rounded-full filter blur-3xl animate-pulse"></div>

  <div className="max-w-7xl mx-auto relative z-10 flex flex-col md:flex-row items-stretch justify-between gap-10">
    {/* Text content */}
    <AnimatePresence mode="wait">
      <motion.div
        key={index}
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.8 }}
        className="text-center md:text-left md:w-1/2 flex flex-col justify-center"
      >
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold mb-6 leading-tight">
          <span
            className={`text-transparent bg-clip-text bg-gradient-to-r ${slide.gradient}`}
          >
            {slide.title1}
          </span>
          <br />
          <span className="text-white">{slide.title2}</span>
        </h1>

        <p className="text-lg md:text-xl text-gray-300 mb-10 relative z-10">
          {slide.desc}
        </p>

        <Link to="/categories">
          <button
            className={`px-8 py-4 bg-gradient-to-r ${slide.gradient} rounded-full text-white font-bold tracking-wide hover:shadow-lg hover:shadow-cyan-500/30 transition-all transform hover:scale-105`}
          >
            EXPLORE COLLECTION
          </button>
        </Link>
      </motion.div>
    </AnimatePresence>

    {/* Image section */}
    <AnimatePresence mode="wait">
      <motion.div
        key={`img-${index}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.8 }}
        className="md:w-1/2 flex justify-center items-center"
      >
        <div className="w-full max-w-[600px] h-full flex items-center justify-center">
          <img
            src={slide.img}
            alt="Gaming banner"
            className="w-full h-auto max-h-[500px] object-contain drop-shadow-[0_0_30px_rgba(147,51,234,0.3)] rounded-2xl"
          />
        </div>
      </motion.div>
    </AnimatePresence>
  </div>

  {/* Navigation dots */}
  <div className="flex justify-center mt-12 space-x-3 relative z-10">
    {slides.map((_, i) => (
      <button
        key={i}
        onClick={() => setIndex(i)}
        className={`w-3 h-3 rounded-full transition-all ${
          i === index ? "bg-cyan-400 scale-125" : "bg-gray-500/40"
        }`}
      />
    ))}
  </div>
</section>

      {/* Featured Products */}
      <FeaturedProducts />

      {/* About Us */}
      <section id="about" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                  ABOUT
                </span>{" "}
                GAME ZTUDIO
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                We are passionate gamers serving the Tamil Nadu gaming community
                since 2018. From the latest PlayStation and Xbox consoles to
                rare collectible games, we've got everything a gamer needs.
              </p>
              <p className="text-gray-400 mb-8">
                Our mission is to make gaming accessible and affordable for
                everyone. We believe in quality products, honest pricing, and
                exceptional customer service.
              </p>
              <div className="flex space-x-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-cyan-400">5000+</div>
                  <div className="text-gray-400">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">
                    1000+
                  </div>
                  <div className="text-gray-400">Games Available</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">24/7</div>
                  <div className="text-gray-400">Support</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-2xl p-8 border border-gray-600">
                <div className="text-center">
                  <div className="text-6xl mb-4">🏆</div>
                  <h3 className="text-2xl font-bold mb-4">Trusted by Gamers</h3>
                  <p className="text-gray-400">
                    Join thousands of satisfied customers who trust us for their
                    gaming needs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section id="services" className="py-20 px-6 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
              OUR SERVICES
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Consoles */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-cyan-400/50 transition-all hover:shadow-xl hover:shadow-cyan-500/10">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-3xl">🎮</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Gaming Consoles</h3>
                <p className="text-gray-400 mb-6">
                  Latest PlayStation 5, Xbox Series X/S, Nintendo Switch and
                  more
                </p>
                <ul className="text-left space-y-2 text-gray-300">
                  <li>• PlayStation 5 & PS4</li>
                  <li>• Xbox Series X/S & Xbox One</li>
                  <li>• Nintendo Switch OLED</li>
                  <li>• Retro Gaming Consoles</li>
                </ul>
              </div>
            </div>

            {/* Game CDs */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-purple-400/50 transition-all hover:shadow-xl hover:shadow-purple-500/10">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-3xl">💿</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Game CDs & Digital</h3>
                <p className="text-gray-400 mb-6">
                  Huge collection of latest and classic games for all platforms
                </p>
                <ul className="text-left space-y-2 text-gray-300">
                  <li>• Latest AAA Game Releases</li>
                  <li>• Classic & Retro Games</li>
                  <li>• Digital Game Codes</li>
                  <li>• Pre-order Games</li>
                </ul>
              </div>
            </div>

            {/* Accessories */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-green-400/50 transition-all hover:shadow-xl hover:shadow-green-500/10">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-green-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-3xl">🎧</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Gaming Accessories</h3>
                <p className="text-gray-400 mb-6">
                  Premium controllers, headsets, and gaming gear
                </p>
                <ul className="text-left space-y-2 text-gray-300">
                  <li>• Wireless Controllers</li>
                  <li>• Gaming Headsets</li>
                  <li>• Gaming Keyboards & Mouse</li>
                  <li>• Console Storage & Stands</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Customer Reviews */}
      <section className="py-20 px-6 ">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
              CUSTOMER REVIEWS
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400">
                    ⭐
                  </span>
                ))}
              </div>
              <p className="text-gray-300 mb-4">
                Amazing ambience. Feels like a gaming cafe. Really good PC ,with an Xbox, ps5 and The rates are affordable. The people running it are very welcoming, cool, and kind as well. Had great time here and will visit again❤️✨
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">R</span>
                </div>
                <div>
                  <div className="font-bold">Richie</div>
                  <div className="text-sm text-gray-400">Tirunelveli</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400">
                    ⭐
                  </span>
                ))}
              </div>
              <p className="text-gray-300 mb-4">
                the gaming session was straight-up 10/10, no cap. Everything was on point, and the staff treated us so well. Went with frnds, and we had a blast. Definitely gonna make it a regular thing from now on. Super stoked for the next time! 🎮👾
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">M</span>
                </div>
                <div>
                  <div className="font-bold">Moses</div>
                  <div className="text-sm text-gray-400">Tirunelveli</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400">
                    ⭐
                  </span>
                ))}
              </div>
              <p className="text-gray-300 mb-4">
               Stable internet connection and peaceful gaming experience.Owner is very friendly towards the customers.New games and collections are added every day and your choices for games are also considered by the owner. So a must try spot to all gamers in tirunelveli.
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-green-400 to-cyan-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">VK</span>
                </div>
                <div>
                  <div className="font-bold">Vickie K</div>
                  <div className="text-sm text-gray-400">Tirunelveli</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Specialities */}
      <section className="py-20 px-6 bg-gray-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
              WHY CHOOSE US
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-800/50 rounded-2xl border border-gray-700 hover:border-cyan-400/30 transition-all">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">🎮</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Authentic Products</h3>
              <p className="text-gray-400">
                100% genuine consoles and games with official warranty
              </p>
            </div>

            <div className="text-center p-6 bg-gray-800/50 rounded-2xl border border-gray-700 hover:border-cyan-400/30 transition-all">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Fast Delivery</h3>
              <p className="text-gray-400">
                Quick delivery across Tamil Nadu with secure packaging
              </p>
            </div>

            <div className="text-center p-6 bg-gray-800/50 rounded-2xl border border-gray-700 hover:border-cyan-400/30 transition-all">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-green-400 to-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">💰</span>
              </div>
              <h3 className="text-xl font-bold mb-3">Best Prices</h3>
              <p className="text-gray-400">
                Competitive pricing with amazing deals and offers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
              GET IN TOUCH
            </span>
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Ready to level up your gaming experience? Contact us today!
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center"  onClick={() => window.location.href = 'mailto:info@gameztudio.com'}>
              <div
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">📧</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Email Us</h3>
               
              <p className="text-cyan-400">info@gameztudio.com</p>
            </div>

            <div className="text-center"  onClick={() => window.location.href = 'tel:+918098044400'}>
              <div
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-bold mb-2">Call Us</h3>
              <p className="text-purple-400">+91 80980 44400</p>
            </div>

            <div className="text-center" onClick={() => window.open('https://wa.me/918098044400', '_blank')}>
              <div  
              className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-green-400 to-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-2xl">💬</span>
              </div>
              <h3 className="text-xl font-bold mb-2">WhatsApp</h3>
              <p className="text-green-400">++91 80980 44400</p>
            </div>
          </div>
          <Link to="/categories">
            <button className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full text-white font-bold tracking-wide hover:shadow-lg hover:shadow-cyan-500/30 transition-all transform hover:scale-105">
              START SHOPPING
            </button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Homepage;
